/**
 * The settlement layer: villagers, jobs, construction, production, upkeep,
 * population and morale.
 *
 * Villagers are not micromanaged. They pick the nearest job that needs doing,
 * walk to it, and work. Construction always outranks harvesting, because a
 * half-built wall at dusk is the most expensive object in the game.
 */

import {
  ARREARS_DESERTION_SECONDS,
  BUILD_TIME_MUL,
  FOOD_KEYS,
  FOOD_PER_MOUTH_PER_RUNG,
  FOOD_VALUE,
  GRAIN_PER_POP_PER_MIN,
  HARVEST_RADIUS,
  HAUL_BUILD_PENALTY,
  HAUL_NOMINAL_TRAVEL,
  HAUL_PATIENCE,
  MAP_H,
  MAP_W,
  MAX_BUILDINGS,
  MAX_UNITS,
  POP_PER_RUNG,
  REPAIR_COST_FRACTION,
  TICK_DT,
  VILLAGER_SHARE,
} from '../constants.ts';
import {
  areHostile,
  Job,
  Phase,
  taxBand,
  Team,
  UF,
  UNCAPPED_RESOURCES,
  UnitState,
  type ResourceBundle,
  type ResourceKey,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
} from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import {
  buildingCentre,
  recomputeManaCap,
  recomputePopCap,
  recomputeStorage,
  spawnUnit,
  statsOf,
  tileBlocks,
  type World,
} from '../world.ts';
import { race, raceEconomyMask } from '../../data/races.ts';
import { DamageType } from '../types.ts';
import { damageBuilding, killUnit } from './combat.ts';
import {
  consecrationSeconds,
  mootPress,
  MOOT_BREED_SECONDS,
  MOOT_PRESS_DAMAGE,
  nameVaultCap,
} from './bindings.ts';
import { agelessPeople, jobHands, markAgedDeath } from './demography.ts';
import { ADULT_MONTHS, BIRTH_MONTHS, CHILDHOOD_MONTHS, ELDER_MONTHS } from '../../data/demography.ts';

/**
 * What one point of a unit's `threat` costs the pool.
 *
 * `threat` rather than a per-unit number, because it is already the game's one
 * honest measure of how much trouble a soldier is — the director budgets waves
 * in it and wages are derived from it — so a pool priced in threat buys the same
 * amount of *army* whichever units it is spent on. A second hand-authored cost
 * column would drift from the first the week somebody rebalanced a Crypt Knight,
 * and the drift would show up as one race quietly fielding twice what it should.
 *
 * The constant is what turns threat into a pool size worth reading. **The
 * sentence that used to be here was stale and it cost the Litany every run it
 * ever played**: it said the bare `MANA_BASE` of 60 was "about two starting
 * companies, the same opening a housed people gets out of the Hearth's
 * twenty-six beds". Establishments were tripled after it was written and the
 * Hearth went to eighteen beds after that, and by then 60 was not two companies
 * — it was not one model of most things. See `MANA_BASE`, which is now derived
 * from the parity the sentence was always claiming rather than asserting it.
 */
export const MANA_PER_THREAT = 1.6;
import { alliedMask, hostileMask } from '../spatial.ts';
import { buildingType } from '../../data/buildings.ts';
import { errandOf, fillAttractions, makeAttractions, steerErrand } from './villagers.ts';
import { harvestKind, nodeKind, type NodeKindDef } from '../../data/nodes.ts';
import { harvestDropPoint, harvestNodeAtPlacement, harvestSourcesAtPlacement, harvestWalkTarget, harvestWorkPoint, removeNode, takeFromNode } from './nodes.ts';
import { districtBonus } from './districts.ts';
import { MONTH_SECONDS, season, seasonUpkeep, seasonYield } from './calendar.ts';
import { awardHeroXp, HERO_XP_PER_BUILDING } from './hero.ts';
import { unitType, unitWage, VILLAGER_TYPE } from '../../data/units.ts';
import { waveModifier } from './director.ts';
import { clamp, clamp01 } from '../../core/math.ts';
import { yieldMul } from '../campaign/commonwealth.ts';

/**
 * Everything at war with the town, and everything on the town's own side.
 *
 * These were `teamMask(Team.Invader)` and `teamMask(Team.Player)` — one banner
 * apiece, which was the whole of each side while there were only two. They are
 * alliance masks now, so a second horde is something a villager runs from and a
 * second banner is not. A mask only ever *prunes* cells the predicate could not
 * accept anyway, so widening one can never change an answer — see `spatial.ts`.
 */
const HOSTILE_TO_TOWN = hostileMask(Team.Player);
const TOWN_MASK = alliedMask(Team.Player);
/** Far enough to cover any map; `nearest` gives up the moment it has an answer. */
const HIRE_RANGE = MAP_W + MAP_H;

const JOB_REASSIGN_TICKS = 15;
/**
 * How many passes it takes for every villager to be re-offered a job.
 *
 * Eight passes at fifteen ticks apiece is four seconds — slow enough that the
 * cost of the rebalance is an eighth of what it was, fast enough that a town
 * still visibly redistributes itself around a new building.
 */
const REBALANCE_SLICES = 8;
const POP_GROWTH_SECONDS = 14;

/** Scratch for `assignJobs`: who has already been given a job this pass. */
const hired = new Uint8Array(MAX_UNITS);

/**
 * A uniform grid over the buildings that still want workers.
 *
 * The standard answer to "nearest thing, ten thousand times a second": bucket
 * the candidates by cell, then search outward one ring at a time and stop as
 * soon as the next ring cannot beat what you have. It is the same structure as
 * `SpatialHash`, sized to buildings rather than units, and it exists separately
 * because vacancies are consumed *during* the sweep — a building that fills up
 * has to stop being an answer for everybody after, which a rebuilt-per-tick
 * structure cannot express.
 *
 * Rebuilt once per job pass and reused across every villager in it, so the cost
 * of the structure is paid once against thousands of queries.
 */
const JOB_CELL = 8;
const JOB_CELLS_X = Math.ceil(MAP_W / JOB_CELL);
const JOB_CELLS_Y = Math.ceil(MAP_H / JOB_CELL);

class JobGrid {
  /** Buildings with a vacancy, bucketed by cell. */
  private readonly cells: number[][] = Array.from({ length: JOB_CELLS_X * JOB_CELLS_Y }, () => []);
  private readonly touched: number[] = [];
  private readonly cx = new Float32Array(MAX_BUILDINGS);
  private readonly cy = new Float32Array(MAX_BUILDINGS);
  private readonly vacancy = new Int32Array(MAX_BUILDINGS);
  private live = 0;

  build(w: World): void {
    const b = w.buildings;
    for (const c of this.touched) this.cells[c].length = 0;
    this.touched.length = 0;
    this.live = 0;

    for (let k = 0; k < b.slots.high; k++) {
      this.vacancy[k] = 0;
      if (b.slots.alive[k] === 0) continue;
      if (b.team[k] !== Team.Player) continue;
      if (b.build[k] < 1) continue; // construction sites are staffed above
      const def = buildingType(b.typeId[k]);
      if (def.workers <= 0) continue;
      const room = def.workers - b.workers[k];
      if (room <= 0) continue;
      this.vacancy[k] = room;
      this.cx[k] = b.tx[k] + def.w / 2;
      this.cy[k] = b.ty[k] + def.h / 2;
      const c = cellOf(this.cx[k], this.cy[k]);
      if (this.cells[c].length === 0) this.touched.push(c);
      this.cells[c].push(k);
      this.live++;
    }
  }

  take(k: number): void {
    if (--this.vacancy[k] <= 0) this.live--;
  }

  /** Nearest building with a vacancy, or -1. */
  nearest(x: number, y: number): number {
    if (this.live <= 0) return -1;
    const cx0 = clampCell(Math.floor(x / JOB_CELL), JOB_CELLS_X);
    const cy0 = clampCell(Math.floor(y / JOB_CELL), JOB_CELLS_Y);
    const maxRing = Math.max(JOB_CELLS_X, JOB_CELLS_Y);
    let best = -1;
    let bestD2 = Infinity;

    for (let ring = 0; ring <= maxRing; ring++) {
      const xLo = cx0 - ring;
      const xHi = cx0 + ring;
      const yLo = cy0 - ring;
      const yHi = cy0 + ring;
      for (let cy = yLo; cy <= yHi; cy++) {
        if (cy < 0 || cy >= JOB_CELLS_Y) continue;
        const onYEdge = cy === yLo || cy === yHi;
        for (let cx = xLo; cx <= xHi; cx++) {
          if (cx < 0 || cx >= JOB_CELLS_X) continue;
          if (!onYEdge && cx !== xLo && cx !== xHi) continue;
          const bucket = this.cells[cy * JOB_CELLS_X + cx];
          for (let n = 0; n < bucket.length; n++) {
            const k = bucket[n];
            if (this.vacancy[k] <= 0) continue;
            const dx = this.cx[k] - x;
            const dy = this.cy[k] - y;
            const d2 = dx * dx + dy * dy;
            if (d2 < bestD2) {
              bestD2 = d2;
              best = k;
            }
          }
        }
      }
      // Nothing in ring+1 can be closer than `ring` cells away.
      if (best >= 0 && bestD2 <= (ring * JOB_CELL) * (ring * JOB_CELL)) break;
    }
    return best;
  }
}

function cellOf(x: number, y: number): number {
  return clampCell(Math.floor(y / JOB_CELL), JOB_CELLS_Y) * JOB_CELLS_X + clampCell(Math.floor(x / JOB_CELL), JOB_CELLS_X);
}

function clampCell(v: number, span: number): number {
  return v < 0 ? 0 : v >= span ? span - 1 : v;
}

const jobGrid = new JobGrid();

export function stepSettlement(w: World, dt: number): void {
  if (w.tick % JOB_REASSIGN_TICKS === 0 || w.dirty.jobs) assignJobs(w);
  stepVillagers(w, dt);
  // Preset villages are campaign producers. A visit still animates residents
  // and repair crews, but cannot mint a second income, workforce or upkeep bill.
  if (w.config.villageType) return;
  stepProduction(w, dt);
  stepUpkeep(w, dt);
  stepTreasury(w, dt);
  stepPopulation(w, dt);
  stepDemography(w, dt);
  stepBindings(w, dt);
  stepMoraleAggregate(w, dt);
}

/**
 * The life cycle, on the Long Ledger's clock.
 *
 * Immediately after `stepPopulation` and for the same reason `stepBindings`
 * is: every line here reads `w.counts.villagers`, and reading last tick's
 * headcount would age a town against a roll it is no longer standing on.
 * Before `stepBindings`, so the press and the groves see a warren the grave
 * has already visited this tick.
 *
 * The rates are continuous — each band sheds `dt / (months × MONTH_SECONDS)`
 * of itself, which *is* the "one part in N per month" transfer stated at the
 * constants, applied smoothly instead of on a month-boundary spike. Smooth on
 * purpose: a monthly lump would need a stored "last month processed" cursor
 * (more hashed state), would fire forty deaths in one tick on a big town, and
 * would make the first of the month the only day anything happens. The
 * integral over a month is the same number either way.
 *
 * Births *are* here, and they are the one flow that does not add a head. The
 * roll is the Hearth's to set and the beds' to cap; this function only ever
 * moves people between bands and out of the world, so growth beyond housing is
 * exactly as impossible as it was. See `src/data/demography.ts` for why a head
 * arriving to fill a bed is a settler rather than a newborn, and what booking
 * it as a newborn cost when it was tried.
 */
function stepDemography(w: World, dt: number): void {
  const d = w.demography;
  /**
   * The Litany. Raised, not born: nothing matures because nothing was ever a
   * child, and nothing dies of age because everything already did. The bands
   * are pinned to zero rather than merely skipped so that a Chronicle card, a
   * debug write or an old save's migration cannot leave a phantom cradle on a
   * people that has no doors for it — the same belt-to-the-braces the economy
   * mask uses on resources a race cannot hold.
   */
  if (agelessPeople(w)) {
    d.children = 0;
    d.elders = 0;
    d.grave = 0;
    return;
  }

  /**
   * Reconcile the bands against the living before aging them. Combat charges
   * each villager death to the bands proportionally through `onVillagerDeath`,
   * but removals that never reach `killUnit` (the warren's musters, the
   * Hollow's toll) and floating-point drift can leave the dependants counted
   * above the roll; scale them back onto it so `adults = pop − c − e` can
   * never be a lie for longer than a tick.
   */
  const v = w.counts.villagers;
  const dependants = d.children + d.elders;
  if (dependants > v) {
    const scale = v > 0 ? v / dependants : 0;
    d.children *= scale;
    d.elders *= scale;
  }

  /**
   * Births, charged against the grown and gated on the same granary the
   * Hearth's growth clock is gated on — a town counting its neighbours is not
   * having children either, and it is that shared gate that lets a test hold
   * the bands still by emptying the stores.
   *
   * Taken before maturation so a tick's newborns are not aged in the same tick
   * they were born in; the integral over a month is unchanged either way, but
   * the other order quietly shortens childhood by a tick's worth every tick.
   */
  if (fed(w)) {
    const parents = Math.max(0, v - d.children - d.elders);
    d.children += parents * (dt / (BIRTH_MONTHS * MONTH_SECONDS));
  }

  // Coming of age. Nothing to add to: adults are the remainder.
  d.children -= d.children * (dt / (CHILDHOOD_MONTHS * MONTH_SECONDS));

  // Grown hands going grey, off the adult *villagers* — soldiers are the
  // companies' to lose, and a company does not thin because a year turned.
  const grown = Math.max(0, v - d.children - d.elders);
  d.elders += grown * (dt / (ADULT_MONTHS * MONTH_SECONDS));

  /**
   * The grave. The band pays fractionally as the debt accrues and the roll
   * pays a whole head when it crosses one, so `elders` and the unit store
   * agree in the integral without a person ever being tracked. Capped at two
   * deaths, exactly as the Hollow's toll is and for the stated reason there:
   * the debt only stands while there is nobody to take, and an unbounded
   * float in hashed state drifts in the last place eventually.
   */
  const dying = d.elders * (dt / (ELDER_MONTHS * MONTH_SECONDS));
  d.elders = Math.max(0, d.elders - dying);
  d.grave = Math.min(d.grave + dying, 2);
  while (d.grave >= 1) {
    const i = oldestVillagerSlot(w);
    if (i < 0) break; // nobody left to bury; the debt stands, capped above
    // `markAgedDeath`: this death settled its band the moment it accrued, so
    // the proportional charge in `killUnit` must stand aside for it.
    markAgedDeath(true);
    killUnit(w, i);
    markAgedDeath(false);
    d.grave -= 1;
    w.dirty.jobs = true;
    pushLog(w.events, 'One of the old ones does not wake. The bell is rung once.', LogTone.Neutral);
  }
}

/**
 * Which villager the grave takes: the highest live slot, the same end of the
 * store the grove's dieback reaps from. The bands are aggregates, so no slot
 * *is* an elder — but the job gate employs the first `adultVillagers` in slot
 * order, so taking from the top takes from the band the gate already treats
 * as past working, and a funeral does not sack a mason. A pure function of
 * hashed state, identical in every replay.
 */
function oldestVillagerSlot(w: World): number {
  const u = w.units;
  for (let i = u.slots.high - 1; i >= 0; i--) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    return i;
  }
  return -1;
}

/**
 * The half of a binding that is a clock rather than a gate.
 *
 * After `stepPopulation`, deliberately: every arm here reads `w.pop` or
 * `w.counts.villagers`, and reading last tick's headcount would make the
 * Splitmoot's press lag the warren by a frame and the Choir's dieback lag winter
 * by one. Before `stepMoraleAggregate` for the same reason in the other
 * direction — a settlement that lost heads this tick should have the crowding
 * term recomputed against what is left.
 *
 * One arm per binding and a `default` that does nothing, which is what makes an
 * unbuilt binding cost nothing rather than misbehave.
 */
function stepBindings(w: World, dt: number): void {
  switch (race(w.raceId).binding) {
    case 'names':
      stepConsecration(w, dt);
      break;
    case 'groves':
      stepGroves(w);
      break;
    case 'moot':
      stepPress(w, dt);
      break;
    default:
      break;
  }
}

/**
 * The Vigil's scribe, and there is exactly one of him.
 *
 * The whole binding is that this clock cannot be bought. Every other people
 * answers "I want another company" with a purse; the Vigil answers it with a
 * week, and the only levers on the week are the Scriptorium and the `vig_naming`
 * node — both of which shorten it and neither of which parallelises it.
 *
 * `nameWork` is *subtracted* rather than zeroed, so raising a Scriptorium
 * mid-name shortens the name being written rather than restarting it, and a
 * saved run resumes where it stopped.
 */
function stepConsecration(w: World, dt: number): void {
  if (w.consecratedNames >= nameVaultCap(w)) return;
  w.nameWork += dt;
  const need = consecrationSeconds(w);
  if (w.nameWork < need) return;
  w.nameWork -= need;
  w.consecratedNames++;
  pushLog(w.events, 'A name is finished, and set aside for whoever earns it.', LogTone.Good);
}

/**
 * The grove clock, which is the season's clock.
 *
 * Two jobs, and the first is easy to miss: **`recomputePopCap` is event-driven**
 * — it runs when a building finishes, falls or is upgraded — and the Choir's cap
 * also moves when nothing at all has happened, because the grove term is
 * seasonal. Without this the ceiling would step down on whatever the next
 * building happened to be rather than when winter arrived. Half-second cadence:
 * it is a walk of the building store, and the season moves once in two hundred
 * and eighty simulated seconds.
 *
 * The second is the bereavement. Hardfrost lowers the cap under a population
 * that is already standing there, and for everybody else a cap you are over is
 * a morale penalty and nothing more. Here they actually die back, one at a time
 * and slowly enough to be watched — which is what the passive means by "Hardfrost
 * takes a share of them back", and what makes a Choir's autumn a decision about
 * how many mouths to arrive at winter with.
 */
function stepGroves(w: World): void {
  if (w.tick % 30 === 0) recomputePopCap(w);
  if (season(w).id !== 'hardfrost') return;
  if (w.tick % GROVE_DIEBACK_TICKS !== 0) return;
  if (w.pop <= w.popCap) return;
  const u = w.units;
  for (let i = u.slots.high - 1; i >= 0; i--) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    killUnit(w, i);
    w.dirty.jobs = true;
    pushLog(w.events, 'One of the groves does not come back. Neither does what it carried.', LogTone.Bad);
    return;
  }
}

/**
 * The press: what happens when nothing is stopping you.
 *
 * The Splitmoot is the only people with no muster cap at all, and this is the
 * price of it. Above the town's tolerance the surplus is not idle, it is a
 * crowd — and a crowd in a warren pulls down whatever is nearest until there is
 * room. Damage scales with how far over the line the warren is, so a settlement
 * ten heads over loses a shed slowly and one a hundred over is being dismantled.
 *
 * The Hearth is exempt, and that is a design decision rather than a safeguard:
 * the mob wrecking the Hearth would end the run through a door the player has no
 * verb to close, and "you lost because your own people were too enthusiastic" is
 * a defeat screen nobody learns anything from. Everything else is fair game.
 *
 * The answer is the muster. Releasing a company takes its models straight out of
 * the warren (see `payMusterBinding`), so the loop closes: breed freely, feel the
 * press, arm it, point it at somebody.
 */
function stepPress(w: World, dt: number): void {
  const over = mootPress(w);
  if (over <= 0) return;
  if (w.tick % PRESS_TICKS !== 0) return;
  const b = w.buildings;
  // Deterministic and spread out: walk from a cursor that advances with the
  // clock, so the mob does not sit on one shed for a minute and then move on.
  const span = Math.max(1, b.slots.high);
  const start = (w.tick / PRESS_TICKS) % span | 0;
  for (let n = 0; n < span; n++) {
    const i = (start + n) % span;
    if (b.slots.alive[i] === 0) continue;
    if (b.team[i] !== Team.Player || i === w.hearth) continue;
    damageBuilding(w, i, over * MOOT_PRESS_DAMAGE * dt * PRESS_TICKS, DamageType.Blunt);
    if (w.tick % (PRESS_TICKS * 40) === 0) {
      pushLog(w.events, 'There is no room. They have started on the walls of somewhere.', LogTone.Bad);
    }
    return;
  }
}

/**
 * Is there enough in the stores for the town to grow and to breed?
 *
 * One function rather than the same pair of comparisons in three places: the
 * Hearth's growth clock, the warren's, and the birth rate in `stepDemography`
 * all mean the same "there is food in the town" and must keep meaning it
 * together, or `tests/lifecycle.test.ts` can empty the granary to hold the roll
 * still and find the cradle filling anyway.
 */
function fed(w: World): boolean {
  /**
   * Twenty-five grain, in grain-equivalents, off any shelf.
   *
   * It read `grain > 25 || bread > 10` — the same threshold twice, at bread's
   * old exchange rate, written out. With four kinds that would have been four
   * comparisons and a rate to keep in step with `FOOD_VALUE` by hand, and a town
   * living on fish would have been unable to grow while its store was full: the
   * exact bug the one-function comment above exists to prevent, arriving through
   * a door nobody had thought to lock.
   */
  return larderWorth(w) > 25;
}

/** Ticks between one grove's worth of heads going back into the ground. */
const GROVE_DIEBACK_TICKS = 150;
/** Ticks between the mob picking a building. */
const PRESS_TICKS = 30;

/**
 * Assign every idle villager to the nearest building that wants hands.
 * Construction sites first, then understaffed producers.
 */
function assignJobs(w: World): void {
  const u = w.units;
  const b = w.buildings;

  // Recount staffing from scratch: impossible to drift, and cheap — it is a
  // walk of the building store, not of the population.
  for (let i = 0; i < b.slots.high; i++) if (b.slots.alive[i] === 1) b.workers[i] = 0;

  /**
   * Nobody is sacked and rehired every fifteen ticks any more.
   *
   * The pass used to strip every villager of its job and place the whole town
   * again from scratch. At three hundred hands that is nothing; at fifteen
   * thousand it was a fifty-millisecond stall landing twice a second — the
   * average tick said 30 Hz and the game hitched anyway, which is exactly the
   * failure mode a mean hides.
   *
   * So the re-offer is time-sliced: a villager whose job is still a job keeps
   * it, unless this is its turn to be asked again. The turn comes round by unit
   * index plus the pass number, so every hand is re-offered inside eight passes
   * — about four seconds — and the slice is a pure function of `w.tick`, never
   * of anything the clock knows. Somebody whose workplace burned down is
   * re-placed immediately regardless of whose turn it is, which is the case
   * that actually matters.
   *
   * What this costs is migration speed: a villager standing next to a newly
   * built granary may take a few seconds to notice it. What it buys is that the
   * settlement layer no longer scales with population × buildings every pass.
   */
  const rebalance = w.tick % JOB_REASSIGN_TICKS === 0;
  const phase = ((w.tick / JOB_REASSIGN_TICKS) | 0) % REBALANCE_SLICES;

  /**
   * Only adults staff jobs. The bands are aggregates, so *which* villagers are
   * the grown is answered by ordinal: the first `hands` live villagers in slot
   * order are the workforce, and everyone after is in the cradle or at the
   * fire — stripped of any job they held, marked un-draftable for the gang
   * search below, and left to `stepVillagers`' errands, which is where a town
   * that is not working goes anyway. A pure function of hashed state; the
   * pre-count is exact rather than read off `w.counts`, which is a tick stale
   * on the pass that spawned somebody.
   */
  let liveVillagers = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 1 && u.team[i] === Team.Player && (u.flags[i] & UF.Villager) !== 0) {
      liveVillagers++;
    }
  }
  const hands = jobHands(w, liveVillagers);
  let seen = 0;

  const villagers: number[] = [];
  const pool: number[] = [];
  const dependants: number[] = [];
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if (seen++ >= hands) {
      // Too young or too old for the ladder. `b.workers` was zeroed above, so
      // not counting them *is* the strip.
      u.workSite[i] = -1;
      u.job[i] = Job.Idle;
      dependants.push(i);
      continue;
    }
    villagers.push(i);

    const site = u.workSite[i];
    const stillAJob =
      site >= 0 && b.slots.alive[site] === 1 && b.team[site] === Team.Player && b.hp[site] > 0;
    const myTurn = rebalance && (i + phase) % REBALANCE_SLICES === 0;
    if (stillAJob && !myTurn) {
      b.workers[site]++;
      continue;
    }
    u.workSite[i] = -1;
    u.job[i] = Job.Idle;
    pool.push(i);
  }

  /**
   * A building may now be carrying more hands than it has room for — a
   * construction site takes four and becomes a producer that wants two. Shed
   * the surplus back into the pool rather than let staffing drift above
   * capacity, which `stepProduction` reads as a multiplier.
   */
  for (const i of villagers) {
    const site = u.workSite[i];
    if (site < 0) continue;
    const def = buildingType(b.typeId[site]);
    const cap = b.build[site] < 1 ? 4 : def.workers;
    if (b.workers[site] <= cap) continue;
    b.workers[site]--;
    u.workSite[i] = -1;
    u.job[i] = Job.Idle;
    pool.push(i);
  }

  /**
   * Construction is staffed first, from whoever is nearest, *pulling people off
   * production if it has to*.
   *
   * The first version only offered new sites to villagers who happened to be
   * idle. Since everyone always had a job, foundations sat at 0% with 0 workers
   * forever — you could place a building and simply nobody would ever come to
   * build it. The settlement stopped growing at whatever size it was when the
   * last villager found work, which quietly capped the entire game.
   */
  const sites: number[] = [];
  for (let k = 0; k < b.slots.high; k++) {
    if (b.slots.alive[k] === 0 || b.team[k] !== Team.Player) continue;
    if (b.build[k] < 1) sites.push(k);
  }

  /**
   * Who has already been given a job this pass.
   *
   * A flat marker rather than a `Set`, and — much more to the point — the
   * search for "the nearest free pair of hands" now goes through the spatial
   * hash instead of walking every villager in the town. That inner walk was
   * villagers × gang-slots, and after a siege a settlement can have hundreds of
   * damaged buildings each asking for a repair gang: at twenty thousand hands
   * it was tens of millions of distance tests every fifteen ticks.
   *
   * The tie-break moves from "lowest unit index" to "first in spatial-hash
   * order among the equidistant", which is a pure function of world state and
   * therefore just as deterministic; an exact floating-point tie between two
   * villagers at the same distance from the same site is not a case the game
   * can tell apart anyway.
   */
  for (const i of villagers) hired[i] = 0;
  // The gang search below goes through the spatial hash, which cannot see the
  // bands — so a dependant is marked as already-hired, which is the one flag
  // every draft path checks.
  for (const i of dependants) hired[i] = 1;
  let freeCount = villagers.length;

  const BUILDERS_PER_SITE = 3;
  const REPAIRERS_PER_SITE = 2;
  /** An order is worth twice the hands an idle gang brings. */
  const ORDERED_REPAIRERS_PER_SITE = 4;
  // Never send more than half the town to the scaffolding; somebody has to eat.
  const builderBudget = Math.max(1, Math.floor(villagers.length * 0.5));
  let spent = 0;

  /** Send the nearest `want` free villagers to a site as a work gang. */
  const staff = (site: number, want: number): void => {
    if (freeCount === 0 || spent >= builderBudget) return;
    const def = buildingType(b.typeId[site]);
    const cx = b.tx[site] + def.w / 2;
    const cy = b.ty[site] + def.h / 2;
    for (let n = 0; n < want && spent < builderBudget && freeCount > 0; n++) {
      const best = w.spatial.nearest(
        cx,
        cy,
        HIRE_RANGE,
        u,
        (i) =>
          hired[i] === 0 &&
          u.slots.alive[i] === 1 &&
          (u.flags[i] & UF.Villager) !== 0 &&
          u.team[i] === Team.Player,
        TOWN_MASK,
      );
      if (best < 0) break;
      hired[best] = 1;
      freeCount--;
      spent++;
      // A gang outranks production, so it may take somebody who kept a job this
      // pass. Give their old workplace its slot back before taking them.
      const was = u.workSite[best];
      if (was >= 0 && b.slots.alive[was] === 1) b.workers[was]--;
      u.workSite[best] = site;
      u.job[best] = Job.Builder;
      b.workers[site]++;
    }
  };

  for (const site of sites) staff(site, BUILDERS_PER_SITE);

  /**
   * What the player has actually *asked* for, before anything automatic.
   *
   * Two things an idle gang cannot do. It goes first, so an ordered building is
   * mended before the slot-order sweep spends the builder budget on a shed at
   * the other end of town; and it goes **whatever the phase is**, which makes it
   * the only repair that happens during a Siege. The list is pruned here rather
   * than by whoever finishes the job, because "finished" has three shapes —
   * mended, demolished, knocked flat — and one prune covers all of them.
   */
  if (w.repairOrders.length > 0) {
    const live: number[] = [];
    for (const k of w.repairOrders) {
      if (k < 0 || k >= b.slots.high || b.slots.alive[k] === 0) continue;
      if (b.team[k] !== Team.Player || b.build[k] < 1) continue;
      if (b.hp[k] >= b.maxHp[k] - 0.5) continue;
      if (live.includes(k)) continue;
      live.push(k);
      staff(k, ORDERED_REPAIRERS_PER_SITE);
    }
    if (live.length !== w.repairOrders.length) w.repairOrders = live;
  }

  /**
   * Repair crews.
   *
   * Repair lives in `doSiteWork`, which only ever runs for a villager's own
   * work site — and the ordinary job hunt below skips any building with no
   * production slots. Between them that meant nothing with `workers: 0` could
   * ever be repaired: every tower, house, granary, storehouse and barracks in
   * the settlement stayed at whatever health the last siege left it at, for
   * the rest of the run. A gang is sent explicitly instead, between waves, when
   * there is anything worth mending.
   */
  if (w.phase !== Phase.Siege) {
    for (let k = 0; k < b.slots.high; k++) {
      if (b.slots.alive[k] === 0 || b.team[k] !== Team.Player) continue;
      if (b.build[k] < 1) continue;
      if (b.hp[k] >= b.maxHp[k] - 0.5) continue;
      staff(k, REPAIRERS_PER_SITE);
    }
  }

  /**
   * Everyone else takes the nearest job that still wants hands.
   *
   * This was a full scan of the building store per villager — villagers ×
   * buildings, with a `buildingType` lookup and a `Math.hypot` inside the
   * innermost loop. At three thousand hands and five hundred buildings it was
   * a fifteenth of the frame; at the sizes this pass is meant to hold it is the
   * frame. The buildings that still want hands go into a uniform grid instead
   * and each villager searches outward from where it is standing, which is the
   * same answer reached without looking at a building on the far side of the
   * city.
   *
   * `Math.hypot` becomes a squared distance, which orders identically — the
   * nearest is the nearest either way — and the vacancy count is decremented in
   * place so a filled building drops out of the search for everyone after.
   */
  jobGrid.build(w);
  for (const i of pool) {
    if (hired[i] === 1) continue;
    const best = jobGrid.nearest(u.x[i], u.y[i]);
    if (best >= 0) {
      u.workSite[i] = best;
      u.job[i] = buildingType(b.typeId[best]).job;
      b.workers[best]++;
      jobGrid.take(best);
    }
  }
  w.dirty.jobs = false;
}

/**
 * Work done at a site: construction first, then clearing rubble off the
 * footprint's neighbourhood, then repairs, then actual production labour.
 */
function doSiteWork(w: World, site: number, dt: number): void {
  const b = w.buildings;
  const def = buildingType(b.typeId[site]);
  const c = buildingCentre(w, site);

  if (b.build[site] < 1) {
    /**
     * Builder-seconds, three masons a site, and two multipliers on top.
     *
     * `BUILD_TIME_MUL` is the whole-game pace — see the note on it; the second
     * is the cart ride. A foundation that had to be paid for out of another
     * town's barns carries the share that came off a cart in `b.haul`, and the
     * masons stand about waiting for it in exactly that proportion. Both are
     * divisions rather than a smaller `buildSpeed`, so every "finishes N%
     * faster" in the tech tree, the relics and the leaders keeps meaning
     * exactly what it says against a base that moved.
     */
    const carted = 1 + HAUL_BUILD_PENALTY * w.mods.haulPenalty * b.haul[site];
    const rate =
      (1 / (Math.max(1, def.buildTime) * BUILD_TIME_MUL * carted)) *
      (w.faction.bonuses.buildSpeed ?? 1) *
      w.mods.buildSpeed;
    b.build[site] = Math.min(1, b.build[site] + rate * dt);
    b.hp[site] = Math.min(b.maxHp[site], b.maxHp[site] * (0.25 + 0.75 * b.build[site]));
    if (b.build[site] >= 1) {
      statsOf(w).buildingsBuilt++;
      // A hero who never draws a sword still ought to level for building a town.
      awardHeroXp(w, HERO_XP_PER_BUILDING);
      recomputePopCap(w);
      recomputeStorage(w);
      recomputeManaCap(w);
      w.dirty.jobs = true;
      // A finished building is a *different* neighbour from a foundation:
      // `recomputeDistricts` skips anything still under construction, so
      // without this the warehouse you just completed never pays its
      // neighbours, and — worse — the cached multiplier becomes a function of
      // build history rather than of state, which breaks the save round-trip.
      w.dirty.districts = true;
      pushEvent(w.events, EV.Build, c.x, c.y, 1, b.typeId[site], Team.Player);
      pushLog(w.events, `${def.name} stands.`, LogTone.Good);
    }
    return;
  }

  /**
   * Repair. Only between waves — nobody re-points masonry with a ram at the
   * gate — **unless the player has ordered this one specifically**, which is
   * the whole point of `Cmd.OrderRepair` and the one thing that verb can do
   * that an idle gang cannot. It costs the same materials either way, so an
   * emergency repair is a decision about where the stone goes rather than a
   * free button, and the masons are standing in the open while they do it.
   */
  if ((w.phase !== Phase.Siege || w.repairOrders.includes(site)) && b.hp[site] < b.maxHp[site]) {
    const heal = (b.maxHp[site] / Math.max(4, def.buildTime * 1.5)) * dt;
    const before = b.hp[site];
    b.hp[site] = Math.min(b.maxHp[site], b.hp[site] + heal);
    const frac = (b.hp[site] - before) / Math.max(1, b.maxHp[site]);
    // Repairs cost materials, at a fraction of what the thing cost to build.
    for (const k of RESOURCE_KEYS) {
      const c2 = (def.cost[k] ?? 0) * REPAIR_COST_FRACTION * frac;
      if (c2 > 0) w.res[k] = Math.max(0, w.res[k] - c2);
    }
  }
}

/** Clear one tile of rubble near the villager, if any. Returns true if working. */
function clearNearbyRubble(w: World, i: number, dt: number): boolean {
  const u = w.units;
  const cx = Math.floor(u.x[i]);
  const cy = Math.floor(u.y[i]);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (!w.map.inBounds(x, y)) continue;
      const k = w.map.idx(x, y);
      if (w.map.rubble[k] === 0) continue;
      w.map.rubbleWork[k] -= dt;
      if (w.map.rubbleWork[k] <= 0) {
        // Salvage: a little of the timber and stone comes back out of the pile.
        // Through `grantCapped`, because this runs inside `stepVillagers` —
        // *before* the clamp at the end of `stepProduction` in the same
        // `stepSettlement` call, which used to quietly eat the whole of it.
        const def = buildingType(w.map.rubble[k] - 1);
        grantCapped(w, 'timber', (def.cost.timber ?? 0) * 0.05);
        grantCapped(w, 'stone', (def.cost.stone ?? 0) * 0.05);
        w.map.rubble[k] = 0;
        w.map.rubbleWork[k] = 0;
        w.rubbleCount = Math.max(0, w.rubbleCount - 1);
        w.dirty.flow = true;
        pushEvent(w.events, EV.Build, x + 0.5, y + 0.5, 2, 0, Team.Player);
      }
      u.state[i] = UnitState.Working;
      return true;
    }
  }
  return false;
}

// ── Hauling ────────────────────────────────────────────────────────────────
//
/**
 * Gatherers cut finite nearby sources and carry loads to a workshop edge.
 * Every output is paid at delivery, including Deep Mine stone byproducts.
 * Depleted or inaccessible sources produce nothing. Other production recipes
 * remain in stepProduction. Claims use a bounded local flood once per trip;
 * short walking legs follow clear ground around walls and mineral footprints.
 */

/** Squared distance at which a worker is close enough to swing at a node. */
const HAUL_REACH2 = 1.8 * 1.8;
/**
 * How long a worker who found nothing waits before asking again.
 *
 * The only thing standing between a spent quarry and a ring search per worker
 * per tick. Two seconds is short enough that a camp notices new ground the tick
 * after a wall comes down and long enough that a dry patch costs a thirtieth of
 * what it would otherwise.
 */
const HAUL_RETRY = 2;
/** Compatibility export: empty camps never generate free resources. */
export const DRY_CAMP_YIELD = 0;

/**
 * The kind of node this building's shift works, or −1 for everybody else.
 *
 * Two gates, and both are needed. The job must be a harvesting job — see
 * `harvestKind`, which is keyed on the job for the Choir's sake — and the
 * building must actually produce the thing that kind yields, so a building that
 * grew a Miner's job and no iron in some future catalogue does not silently stop
 * producing whatever it does make.
 */
export function siteHarvestKind(def: ReturnType<typeof buildingType>): number {
  const k = harvestKind(def.job);
  if (k < 0) return -1;
  return (def.produces[nodeKind(k).resource] ?? 0) > 0 ? k : -1;
}

/**
 * Seconds per raw unit at the node, solved so that a camp at the nominal
 * distance matches the old model exactly. See the calibration note above.
 */
function chopRate(def: ReturnType<typeof buildingType>, nd: NodeKindDef): number {
  const perWorker = (def.produces[nd.resource] ?? 0) / Math.max(1, def.workers);
  if (perWorker <= 0) return 0;
  // A load takes `load / perWorker` seconds of a worker's life under the old
  // model; the travel is subtracted from that, and what is left is cutting. The
  // floor keeps a hypothetical building with a huge per-worker rate and a tiny
  // load from solving to a negative or infinite cutting time.
  const chopSeconds = Math.max(1, nd.load / perWorker - HAUL_NOMINAL_TRAVEL);
  return nd.load / chopSeconds;
}

/**
 * Everything `stepProduction` would have multiplied a unit of output by, minus
 * the parts the haul has already accounted for.
 *
 * `staffing` is gone because the haul *is* the staffing — three men make three
 * men's worth of trips — and `dt` is gone because a load is a load. What is left
 * is applied at the storehouse door: tier, placement, the difficulty dial, the
 * faction's rate, the modifier bag and the season. Keeping the season here
 * rather than at the stump is deliberate: Hardfrost shortens the working day,
 * which is a statement about what a shift is worth, not about what a tree holds.
 */
function harvestMultiplier(w: World, site: number, k: ResourceKey): number {
  const b = w.buildings;
  const tierMul = 1 + (b.tier[site] - 1) * 0.45;
  const facRates = w.faction.bonuses.resourceRate ?? {};
  return (
    tierMul *
    districtBonus(w, site) *
    w.difficulty.incomeMul *
    (facRates[k] ?? 1) *
    w.mods.resourceRate *
    // A village works the ground harder than a city does, because working the
    // ground is the whole of what it is for. See `VILLAGE_YIELD`.
    yieldMul(w) *
    seasonYield(w, k)
  );
}

/**
 * Retry delay after an obstructed outward or return leg. The camp pauses rather
 * than inventing income, then tries again when its route may have reopened.
 * A successful delivery clears the pause. The clock uses the gatherer's saved
 * b.stock field, which is separate from the toll of non-gathering factories.
 */
export const HAUL_STALL_SECONDS = 25;

/** Stop this camp trying for a while. See `HAUL_STALL_SECONDS`. */
function markStall(w: World, site: number): void {
  const def = buildingType(w.buildings.typeId[site]), kind = siteHarvestKind(def);
  // A newly hired worker can still be crossing town. Its failed personal leg
  // must not recall the entire shift already cutting beside the workshop.
  if (kind >= 0 && harvestNodeAtPlacement(w, kind, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h) < 0) {
    w.buildings.stock[site] = HAUL_STALL_SECONDS;
  }
}

/** Is this camp stalled — has anybody recently failed to make the round trip? */
export function campStalled(w: World, site: number): boolean {
  return w.buildings.stock[site] > 0;
}

/** Is there anything of this kind left within reach of this camp, and reachable? */
export function campHasNodes(w: World, site: number, kind: number): boolean {
  if (campStalled(w, site)) return false;
  const def = buildingType(w.buildings.typeId[site]);
  return harvestNodeAtPlacement(w, kind, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h) >= 0;
}

/**
 * Which node this worker sets out for.
 *
 * The nearest few to the *camp*, not to the worker, so a shift works one patch
 * and the player can watch it thin; and then the `i % found`-th of them, so
 * three men do not queue at the same trunk. That spread costs nothing and holds
 * no state — it is a pure function of the worker's slot and what is standing.
 */
function claimNode(w: World, i: number, site: number, def: ReturnType<typeof buildingType>, kind: number): number {
  const sources = harvestSourcesAtPlacement(w, kind, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h);
  return sources[i % Math.min(4, sources.length)]?.node ?? -1;
}

/** Tip the basket into the stores. Returns what actually stuck. */
function dropLoad(w: World, i: number, site: number, nd: NodeKindDef): number {
  const u = w.units;
  const raw = u.carry[i];
  if (!(raw > 0)) return 0;
  const def = buildingType(w.buildings.typeId[site]);
  const primary = def.produces[nd.resource] ?? 0;
  let paidRaw = raw;
  for (const k of RESOURCE_KEYS) {
    const ratio = primary > 0 ? (def.produces[k] ?? 0) / primary : 0;
    if (ratio > 0 && !UNCAPPED_RESOURCES.includes(k)) paidRaw = Math.min(paidRaw, storageRoom(w, k) / (ratio * harvestMultiplier(w, site, k)));
  }
  if (!(paidRaw > 0)) return 0;
  u.carry[i] = Math.max(0, raw - paidRaw);
  if (u.carry[i] < 1e-4) { u.carry[i] = 0; u.carryKind[i] = 0; }
  let total = 0;
  for (const k of RESOURCE_KEYS) {
    const ratio = primary > 0 ? (def.produces[k] ?? 0) / primary : 0;
    if (ratio <= 0) continue;
    const kept = grantCapped(w, k, paidRaw * ratio * harvestMultiplier(w, site, k));
    statsOf(w).resourcesProduced[k] += kept;
    total += kept;
  }
  return total;
}

/**
 * One worker's tick of hauling.
 *
 * Returns true if it took charge of the man — set his goal, or had him swing at
 * something — and false if there is nothing to haul, in which case the caller
 * falls through to the settlement's ordinary "stand at your workplace"
 * behaviour without generating resources.
 *
 * `u.haulT` is always **the patience left on the leg this man is currently
 * walking**, refilled at the start of each one, with the single exception of a
 * worker standing empty-handed at a camp with nothing to cut — for whom it is
 * the wait before asking again. The two never overlap: the first requires a
 * claimed node or a load, the second requires neither.
 */
function stepHaul(
  w: World,
  i: number,
  site: number,
  def: ReturnType<typeof buildingType>,
  kind: number,
  dt: number,
): boolean {
  const u = w.units;
  const nd = nodeKind(kind);

  const full = u.carry[i] >= nd.load - 1e-4;

  // The claim, validated every tick: a tree somebody else finished is not a
  // destination, and neither is one a wall has since been drawn through.
  let n = u.nodeTarget[i];
  if (n >= 0 && (!w.nodes.isAlive(n) || w.nodes.kind[n] !== kind || w.nodes.amount[n] <= 0)) {
    n = -1;
    u.nodeTarget[i] = -1;
  }

  if (campStalled(w, site)) {
    /**
     * Nobody sets out from a camp that has just failed to make the round trip,
     * and anybody already out is called in — but a man **carrying** still comes
     * home and puts it down, and his delivery clears the stall, because a
     * delivery is the only proof there is that the trip can be made. Without
     * that, a shift caught mid-basket would stand in its own doorway holding
     * timber that nothing would ever collect. See `HAUL_STALL_SECONDS`.
     */
    n = -1;
    u.nodeTarget[i] = -1;
    if (u.carry[i] <= 0) return false;
  } else if (!full && n < 0 && u.carry[i] <= 0) {
    const cx = w.buildings.tx[site] + def.w / 2, cy = w.buildings.ty[site] + def.h / 2;
    // Reach the workplace before claiming its distant patch. The bounded
    // harvest route is designed for a camp-to-source leg, not all of town.
    if ((u.x[i] - cx) ** 2 + (u.y[i] - cy) ** 2 > HARVEST_RADIUS ** 2) return false;
    if (u.haulT[i] > 0) {
      u.haulT[i] -= dt;
    } else {
      n = claimNode(w, i, site, def, kind);
      u.nodeTarget[i] = n;
      u.haulT[i] = n >= 0 ? HAUL_PATIENCE : HAUL_RETRY;
    }
  }

  // Full, or holding a part-load with nowhere left to fill it: go and tip it in.
  if (full || (n < 0 && u.carry[i] > 0)) {
    // Self-healing: whatever the clock was doing before, the walk home gets a
    // full measure of patience. A worker who was three ticks from giving up on a
    // tree must not inherit that and drop his basket on the way in.
    if (u.haulT[i] <= 0) u.haulT[i] = HAUL_PATIENCE;
    const point = harvestDropPoint(w, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h, u.x[i], u.y[i]);
    if (point && (u.x[i] - point.x) ** 2 + (u.y[i] - point.y) ** 2 <= 0.85 ** 2) {
      dropLoad(w, i, site, nd);
      // A delivery is the proof that this camp works. Whatever stall credit it
      // had built up is wrong, and is cleared outright rather than decayed.
      w.buildings.stock[site] = 0;
      u.state[i] = UnitState.Working;
      u.goalX[i] = u.x[i];
      u.goalY[i] = u.y[i];
      return true;
    }
    u.haulT[i] -= dt;
    if (u.haulT[i] <= 0) {
      /**
       * He could not get home.
       *
       * The other half of the walled-in hazard, and the half that is easy to
       * miss: patience on the outward leg saves a man who cannot *reach* the
       * wood, and does nothing at all for one who reached it and was then walled
       * out of his own camp. The load is lost — he put it down somewhere, and
       * there is no state in this game for a pile of timber in a field — and the
       * camp pauses before retrying the obstructed route.
       */
      u.carry[i] = 0;
      u.carryKind[i] = 0;
      u.nodeTarget[i] = -1;
      u.haulT[i] = HAUL_RETRY;
      markStall(w, site);
      return false;
    }
    const target = point && harvestWalkTarget(w, u.x[i], u.y[i], point.x, point.y);
    u.goalX[i] = target?.x ?? u.x[i];
    u.goalY[i] = target?.y ?? u.y[i];
    u.state[i] = UnitState.Moving;
    return true;
  }

  if (n < 0) return false;

  const tile = w.nodes.tile[n];
  const nx = (tile % w.map.w) + 0.5;
  const ny = ((tile / w.map.w) | 0) + 0.5;
  const point = harvestWorkPoint(w, n, u.x[i], u.y[i]);
  if (!point) {
    u.nodeTarget[i] = -1;
    u.haulT[i] = HAUL_RETRY;
    markStall(w, site);
    return false;
  }
  const d2 = (point.x - u.x[i]) ** 2 + (point.y - u.y[i]) ** 2;
  const onRock = w.nodes.solidTiles[Math.floor(u.y[i]) * MAP_W + Math.floor(u.x[i])] === 1;

  if (d2 > (nd.footprint > 1 ? 0.65 ** 2 : HAUL_REACH2) || onRock) {
    // Still walking. The patience clock is the only thing between a node behind
    // a newly closed gate and a worker walking into stone for the rest of the
    // run — see `HAUL_PATIENCE`.
    u.haulT[i] -= dt;
    if (u.haulT[i] <= 0) {
      u.nodeTarget[i] = -1;
      u.haulT[i] = HAUL_RETRY;
      markStall(w, site);
      return false;
    }
    const target = harvestWalkTarget(w, u.x[i], u.y[i], point.x, point.y);
    u.goalX[i] = target?.x ?? u.x[i];
    u.goalY[i] = target?.y ?? u.y[i];
    u.state[i] = UnitState.Moving;
    return true;
  }

  u.haulT[i] = HAUL_PATIENCE;
  u.goalX[i] = u.x[i];
  u.goalY[i] = u.y[i];
  u.state[i] = UnitState.Working;
  const got = takeFromNode(w.nodes, n, Math.min(chopRate(def, nd) * dt, nd.load - u.carry[i]));
  u.carry[i] += got;
  if (got > 0) u.carryKind[i] = RESOURCE_KEYS.indexOf(nd.resource) + 1;
  if (w.nodes.amount[n] <= 0) {
    // The last of it. The node goes, the ground goes back to ash, and the
    // pathing weights change with it — deadwood is slower to cross than plain.
    if (removeNode(w.map, w.nodes, n)) w.dirty.flowWeights = true;
    pushEvent(w.events, EV.Build, nx, ny, 3, kind, Team.Player);
    u.nodeTarget[i] = -1;
    u.haulT[i] = 0;
  }
  return true;
}

/**
 * Point a worker at his workplace: straight there if it is close, along the
 * shared route if it is not.
 *
 * Lifted out of `stepVillagers` so a hauler walking a full basket home takes
 * exactly the road a worker walking to his shift takes, gates and all. One
 * definition of "go to work", two callers.
 */
function steerToSite(
  w: World,
  i: number,
  site: number,
  def: ReturnType<typeof buildingType>,
  cx: number,
  cy: number,
): void {
  const u = w.units;
  const offA = (i % 5) * 1.2566;
  const ring = Math.max(def.w, def.h) * 0.5 + 0.85;
  const standX = cx + Math.cos(offA) * ring;
  const standY = cy + Math.sin(offA) * ring;
  if ((u.x[i] - cx) ** 2 + (u.y[i] - cy) ** 2 < 36) {
    // Nearby roofs still block the last leg. A direct six-tile shortcut can
    // leave every builder leaning against the neighbouring farm forever.
    const target = !tileBlocks(w, Math.floor(standX), Math.floor(standY), i)
      ? harvestWalkTarget(w, u.x[i], u.y[i], standX, standY)
      : null;
    if (target) {
      u.goalX[i] = target.x;
      u.goalY[i] = target.y;
      return;
    }
  } else {
    const route = w.paths.get(w.map, w.map.hearthX, w.map.hearthY, site, Math.floor(cx), Math.floor(cy), w.tick);
    const step = route ? nextWaypoint(w, route, u.x[i], u.y[i]) : null;
    if (step) {
      u.goalX[i] = step.x;
      u.goalY[i] = step.y;
      return;
    }
  }
  // A ring position can itself lie under another building. Use a reachable
  // edge of this site instead; a completely enclosed site gets no false goal.
  const point = harvestDropPoint(w, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h, u.x[i], u.y[i]);
  const target = point && harvestWalkTarget(w, u.x[i], u.y[i], point.x, point.y);
  u.goalX[i] = target ? target.x : u.x[i];
  u.goalY[i] = target ? target.y : u.y[i];
}

/**
 * Where the shrines, the market and the houses are, rebuilt at the top of every
 * villager pass. Scratch: never read across a tick, never saved, never hashed.
 * The argument for rebuilding rather than caching is at the top of
 * `systems/villagers.ts` and it is the same argument as the path-cache note.
 */
const attractions = makeAttractions();

function stepVillagers(w: World, dt: number): void {
  const u = w.units;
  const b = w.buildings;
  fillAttractions(w, attractions);

  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;

    // Villagers flee anything hostile within four tiles. They are not soldiers.
    // The mask is what makes this affordable at city scale: a villager deep
    // inside a town at peace settles the question from the block grid without
    // touching a single unit. See `spatial.ts`.
    const threat = w.spatial.nearest(
      u.x[i],
      u.y[i],
      4.5,
      u,
      (j) => areHostile(Team.Player, u.team[j]),
      HOSTILE_TO_TOWN,
    );
    if (threat >= 0) {
      const dx = u.x[i] - u.x[threat];
      const dy = u.y[i] - u.y[threat];
      const d = Math.hypot(dx, dy) || 1;
      u.goalX[i] = u.x[i] + (dx / d) * 6;
      u.goalY[i] = u.y[i] + (dy / d) * 6;
      u.state[i] = UnitState.Moving;
      continue;
    }

    const site = u.workSite[i];
    if (site < 0 || b.slots.alive[site] === 0) {
      // Idle hands clear rubble first. A town that has just been sacked has
      // better things to do than go to the market, and this is the one errand
      // with a mechanical consequence.
      if (w.rubbleCount > 0 && !w.config.villageType) {
        if (clearNearbyRubble(w, i, dt)) continue;
        const target = nearestRubble(w, u.x[i], u.y[i]);
        if (target >= 0) {
          u.goalX[i] = (target % w.map.w) + 0.5;
          u.goalY[i] = ((target / w.map.w) | 0) + 0.5;
          continue;
        }
      }
      /**
       * Otherwise they have a life.
       *
       * This used to be two lines that parked every idle hand in a seven-by-seven
       * grid on the Hearth, which is why a settlement with more people than jobs
       * — which is *every* settlement past the first ten minutes — looked like a
       * queue rather than a town. `steerErrand` costs a modulo and two array
       * reads and sends them to the shrine, the market, their own door or a
       * neighbour instead, in proportions their people keep.
       *
       * The Hearth drift survives as the fallback, and it is still right: a town
       * of four buildings has no shrine to go to.
       */
      const errand = errandOf(w, i);
      if (steerErrand(w, i, errand, attractions)) continue;
      u.goalX[i] = w.map.hearthX + 0.5 + ((i % 7) - 3) * 0.6;
      u.goalY[i] = w.map.hearthY + 0.5 + ((((i / 7) | 0) % 7) - 3) * 0.6;
      continue;
    }

    const def = buildingType(b.typeId[site]);
    const c = buildingCentre(w, site);

    /**
     * Harvest shifts leave the building.
     *
     * One array read for everybody else: `u.job[i] === def.job` is false for a
     * builder or a repair gang sent to a camp — they build and mend, they do not
     * fell — and `siteHarvestKind` is −1 for every building in the catalogue but
     * four. A finished camp only; a foundation has nobody to haul for yet.
     */
    if (!w.config.villageType && b.build[site] >= 1 && u.job[i] === def.job) {
      const hk = siteHarvestKind(def);
      if (hk >= 0) {
        if (stepHaul(w, i, site, def, hk, dt)) continue;
        const point = harvestDropPoint(w, b.tx[site], b.ty[site], def.w, def.h, u.x[i], u.y[i]);
        const target = point && harvestWalkTarget(w, u.x[i], u.y[i], point.x, point.y);
        if (target) {
          u.goalX[i] = target.x;
          u.goalY[i] = target.y;
          u.state[i] = (target.x - u.x[i]) ** 2 + (target.y - u.y[i]) ** 2 < 0.85 ** 2 ? UnitState.Working : UnitState.Moving;
          continue;
        }
      }
    }

    // Stand just off the footprint rather than inside it.
    steerToSite(w, i, site, def, c.x, c.y);
    const d2 = (u.x[i] - c.x) ** 2 + (u.y[i] - c.y) ** 2;
    const workRange = (Math.max(def.w, def.h) * 0.5 + 1.6) ** 2;
    if (d2 <= workRange) {
      u.state[i] = UnitState.Working;
      if (!w.config.villageType) doSiteWork(w, site, dt);
    } else if (u.state[i] === UnitState.Working) {
      u.state[i] = UnitState.Idle;
    }
  }
}

/**
 * The next point on a shared route: find the closest tile on it, then aim a
 * short way further along. Aiming at the very next tile makes villagers crawl;
 * aiming at the end makes them ignore the route entirely.
 */
function nextWaypoint(w: World, route: number[], x: number, y: number): { x: number; y: number } | null {
  if (route.length === 0) return null;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < route.length; i++) {
    const tx = (route[i] % w.map.w) + 0.5;
    const ty = ((route[i] / w.map.w) | 0) + 0.5;
    const d = (tx - x) ** 2 + (ty - y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  const ahead = Math.min(route.length - 1, best + 4);
  for (let j = ahead; j >= best; j--) {
    // Shared routes end inside their destination building. Workers must stop
    // outside it and must route around corners skipped by the look-ahead.
    if (w.map.building[route[j]] >= 0) continue;
    const tx = (route[j] % w.map.w) + 0.5, ty = ((route[j] / w.map.w) | 0) + 0.5;
    const target = harvestWalkTarget(w, x, y, tx, ty);
    if (target) return target;
  }
  return null;
}

/**
 * Nearest uncleared rubble tile within a sensible walk.
 *
 * Reads the map's rubble index rather than sweeping a 45×45 box of tiles per
 * villager per tick — see the note on `TileMap.rubbleTiles` for why the answer
 * is identical, tie-breaks included, and why the entry is re-checked here.
 */
function nearestRubble(w: World, x: number, y: number): number {
  const R = 22;
  let best = -1;
  let bestD = R * R;
  const { list, n } = w.map.rubbleTiles(w.tick);
  if (n === 0) return -1;
  const mw = w.map.w;

  /**
   * The list is in ascending tile order, which is row-major, so every tile in
   * range lies in one contiguous run of it — the band of rows within R of the
   * villager. A siege leaves thousands of tiles of rubble and a town has
   * thousands of idle hands, and scanning the whole list for each of them is
   * the same quadratic in a different coat. Binary-search the start of the
   * band and stop at the end of it.
   */
  const loTile = Math.max(0, (Math.floor(y) - R)) * mw;
  const hiTile = (Math.min(w.map.h - 1, Math.floor(y) + R) + 1) * mw;
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid] < loTile) lo = mid + 1;
    else hi = mid;
  }

  for (let i = lo; i < n; i++) {
    const k = list[i];
    if (k >= hiTile) break;
    if (w.map.rubble[k] === 0) continue;
    const tx = k % mw;
    const ty = (k / mw) | 0;
    const d = (tx + 0.5 - x) ** 2 + (ty + 0.5 - y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best;
}

/**
 * How much of `k` the settlement could still take in.
 *
 * Mirrors the clamp at the end of `stepProduction` deliberately — a treasury is
 * not a granary and neither is a reliquary, so coin is uncapped and souls get
 * twice the shed space. If that rule ever moves, this moves with it, and both
 * ends of the game agree about what "full" means.
 */
export function storageRoom(w: World, k: ResourceKey): number {
  const cap = k === 'souls'
    ? w.resCap * 2
    : UNCAPPED_RESOURCES.includes(k)
      ? Number.POSITIVE_INFINITY
      : w.resCap;
  return Math.max(0, cap - w.res[k]);
}

/**
 * Hand the settlement `amount` of `k`, and keep only what there is room for.
 * Returns what actually stuck.
 *
 * Every windfall in the game — a demolition refund, salvage out of a rubble
 * pile — used to be added straight into `w.res` and clamped away by
 * `stepProduction` later in the same tick. That is not the same as not being
 * granted: for the rest of that tick the phantom material was real, spendable
 * money, and by the next tick it had never existed. Credit what can be kept,
 * and nothing else.
 */
export function grantCapped(w: World, k: ResourceKey, amount: number): number {
  if (!(amount > 0)) return 0;
  const kept = Math.min(amount, storageRoom(w, k));
  w.res[k] += kept;
  return kept;
}

/**
 * How much of the recipe the stores can actually pay for, and what ran out.
 *
 * Lifted out of `stepProduction` so the panel can ask the same question the
 * simulation asks, with the same arithmetic, rather than a second copy of it
 * that drifts. The throttle was computed and thrown away every tick — a
 * converter standing idle for want of one input is the single most useful thing
 * a player can be told about a chain, and nothing anywhere said it.
 */
export function inputThrottle(
  w: World,
  def: ReturnType<typeof buildingType>,
  staffing: number,
  tierMul: number,
  dt: number,
): { throttle: number; starvedOf: ResourceKey | '' } {
  let throttle = 1;
  let starvedOf: ResourceKey | '' = '';
  for (const k of RESOURCE_KEYS) {
    const need = def.consumes[k] ?? 0;
    if (need <= 0) continue;
    const want = need * staffing * tierMul * dt;
    if (w.res[k] < want) {
      const t = w.res[k] / Math.max(1e-6, want);
      if (t < throttle) {
        throttle = t;
        starvedOf = k;
      }
    }
  }
  return { throttle, starvedOf };
}

/** Fully supplied output per second, shared by the tick and its readout. */
function recipeOutputRate(w: World, def: ReturnType<typeof buildingType>, key: ResourceKey, tierMul: number, placeMul: number): number {
  return (def.produces[key] ?? 0) * tierMul * placeMul * w.difficulty.incomeMul *
    (w.faction.bonuses.resourceRate?.[key] ?? 1) * w.mods.resourceRate * yieldMul(w) * seasonYield(w, key);
}

/** Converters only spend the fraction of a recipe whose finished goods fit. */
export function productionThrottle(
  w: World, def: ReturnType<typeof buildingType>, staffing: number,
  tierMul: number, placeMul: number, dt: number,
): { throttle: number; starvedOf: ResourceKey | ''; storageFull: ResourceKey | ''; limitReason: string } {
  const input = inputThrottle(w, def, staffing, tierMul, dt);
  let throttle = input.throttle;
  let storageFull: ResourceKey | '' = '';
  // Converters protect paid inputs; gatherer readouts mirror the storage gate
  // used by dropLoad, which retains a basket until all its products will fit.
  if (Object.keys(def.consumes).length > 0 || siteHarvestKind(def) >= 0) {
    for (const key of RESOURCE_KEYS) {
      if (!(def.produces[key] ?? 0)) continue;
      // If a recipe consumes its own output, that withdrawal also makes room.
      const net = (recipeOutputRate(w, def, key, tierMul, placeMul) - (def.consumes[key] ?? 0) * tierMul) * staffing * dt;
      if (net <= 0) continue;
      const available = storageRoom(w, key) / net;
      if (available < throttle) { throttle = Math.max(0, available); storageFull = key; }
    }
  }
  const starvedOf = storageFull ? '' : input.starvedOf;
  const limitReason = storageFull ? `Storage ${throttle <= 0 ? 'full' : 'nearly full'}: ${RESOURCE_LABELS[storageFull]}.`
    : starvedOf ? `Needs ${RESOURCE_LABELS[starvedOf]}.` : '';
  return { throttle, starvedOf, storageFull, limitReason };
}

/**
 * Take one head off a Hollow yard's own shift. Returns false if there was
 * nobody there to take, which leaves the debt standing.
 *
 * Off its *own* shift, and that is the whole of the design rather than a
 * convenience. A toll drawn from the town at large would be a tax — a number
 * ticking down somewhere, indistinguishable from upkeep. Drawn from the people
 * standing in the doorway it is a feedback loop the player can watch: the yard
 * de-staffs itself as it works, its own output curves down toward whatever rate
 * the settlement can replace people at, and the answer to wanting more
 * hollowglass is more bread and more beds rather than more converters. A
 * settlement that runs six of these is a settlement that has decided what its
 * population is *for*.
 *
 * Lowest slot index among its own workers, which is a pure function of hashed
 * state and therefore identical in every replay.
 */
function takeToll(w: World, site: number, name: string): boolean {
  const u = w.units;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    if (u.workSite[i] !== site) continue;
    u.kill(i);
    if (w.buildings.workers[site] > 0) w.buildings.workers[site]--;
    w.dirty.jobs = true;
    // Small, and it compounds: the building's own standing morale penalty is
    // the loud part of the bill, and this is the part that arrives per head.
    w.morale -= 0.015;
    pushLog(w.events, `The ${name} is one short again. The list is posted at dusk.`, LogTone.Bad);
    return true;
  }
  return false;
}

function stepProduction(w: World, dt: number): void {
  const b = w.buildings;
  const stats = statsOf(w);
  let goldFromTrade = 0;

  // One lookup for the whole pass.
  const famine = waveModifier(w, 'famine');

  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    if (b.team[i] !== Team.Player) continue;
    const def = buildingType(b.typeId[i]);
    if (def.workers > 0 && b.workers[i] <= 0) continue;

    const staffing = def.workers > 0 ? b.workers[i] / def.workers : 1;
    const tierMul = 1 + (b.tier[i] - 1) * 0.45;
    // Where it stands is worth as much as how many people stand in it.
    const placeMul = districtBonus(w, i);

    // Every gatherer output, including Deep Mine stone, comes from delivered loads.
    const hk = siteHarvestKind(def);
    // The stall runs down a second a second, *after* it has been read, so a camp
    // that was walled off its wood tries again within half a minute of the wall
    // coming down. Decaying before the read would spend the whole stall in the
    // tick it was set.
    if (hk >= 0 && b.stock[i] > 0) b.stock[i] = Math.max(0, b.stock[i] - dt);
    if (hk >= 0) continue;

    // Inputs first — a smelter with no timber runs at a crawl.
    const throttle = productionThrottle(w, def, staffing, tierMul, placeMul, dt).throttle;
    for (const k of RESOURCE_KEYS) {
      const need = def.consumes[k] ?? 0;
      if (need > 0) w.res[k] = Math.max(0, w.res[k] - need * staffing * tierMul * dt * throttle);
    }
    for (const k of RESOURCE_KEYS) {
      const out = def.produces[k] ?? 0;
      if (out <= 0) continue;
      /**
       * Famine Wind: no grain is produced while the wave stands.
       *
       * Exactly what the scouting panel has been promising and never doing.
       * Grain only — bread keeps coming, so a settlement that filled its
       * granary and built an oven rides it out, and one that has been eating
       * the harvest as it comes in does not. That is the whole shape of the
       * modifier: it punishes living hand to mouth, not bad luck.
       */
      if (k === 'grain' && famine) continue;
      /**
       * The season is a multiplier on the ground, not on the building.
       *
       * `seasonYield` moves grain with the fields and timber, stone and iron
       * with the length of the working day; a sawmill, an oven and a counting
       * house are indoors and are left alone. That last part is deliberate — a
       * bakery is the answer to Hardfrost, and a season that shut the bakery
       * would be punishing the preparation instead of the lack of it.
       */
      const gain = recipeOutputRate(w, def, k, tierMul, placeMul) * staffing * throttle * dt;
      w.res[k] += gain;
      stats.resourcesProduced[k] += gain;
      if (k === 'gold') goldFromTrade += gain;
    }

    /**
     * The Hollow's toll.
     *
     * Charged against the *same* effective rate the recipe is — `staffing` and
     * `throttle` and nothing else — so a yard idling for want of ashglass owes
     * heads at exactly the rate it is making hollowglass, and a yard standing
     * empty owes nothing. Placement, tier, season and the difficulty dial are
     * all deliberately left out: they change what the work is worth, not how
     * many people it goes through.
     *
     * The debt is kept in `b.stock`, which is hashed and saved, and it is only
     * discharged when a head is actually taken. A Hollowing whose shift has
     * already been emptied carries the arrears instead of quietly producing for
     * free, and pays them the moment somebody walks back in.
     *
     * Capped at two tolls, and the cap is about the hash rather than about
     * mercy. The arrears only grow while the yard is staffed enough to work and
     * `takeToll` still finds nobody — a narrow window, since the count it reads
     * and the units it walks can disagree for as long as somebody is walking to
     * the door. Narrow is not never, and an unbounded float in hashed state
     * drifts in the last place eventually. Two tolls is more slack than that
     * window can use and still small enough that a yard cannot bank a massacre.
     */
    if (def.toll > 0) {
      const worked = staffing * throttle * dt;
      if (worked > 0) {
        b.stock[i] = Math.min(b.stock[i] + worked, def.toll * 2);
        if (b.stock[i] >= def.toll && takeToll(w, i, def.name)) b.stock[i] -= def.toll;
      }
    }
  }

  w.econ.tradeIncome = dt > 0 ? (goldFromTrade / dt) * 60 : 0;

  // Storage caps. Gold and Souls are hoarded differently: a treasury is not a
  // granary, and neither is a reliquary. Capping coin at warehouse space would
  // make the late game about building sheds.
  /**
   * The economy mask is enforced here, and this is the belt to the catalogue's
   * braces.
   *
   * A people is never *offered* a building that makes what it cannot hold and
   * the market refuses the key outright, so in an ordinary run nothing ever
   * puts Ichor into an Ashen storehouse. This pins it at zero anyway, because
   * "in an ordinary run" is not a guarantee: a Chronicle answer, a relic, a
   * debug `give`, a captured stockpile or a save written by a build that had
   * different ideas are all doors, and a bar that appears for one race because
   * of a card is worse than no bar at all.
   *
   * It costs one set lookup per key per tick against a set that is built once
   * and cached per race. The alternative — checking at every site that can add
   * a resource — is a dozen places that each have to remember, which is the
   * shape of every bug this file has ever had.
   */
  const mask = raceEconomyMask(w.raceId, RESOURCE_KEYS);
  for (const k of RESOURCE_KEYS) {
    if (!mask.has(k)) {
      w.res[k] = 0;
      continue;
    }
    if (!UNCAPPED_RESOURCES.includes(k)) {
      if (w.res[k] > w.resCap) w.res[k] = w.resCap;
    }
    if (w.res[k] < 0) w.res[k] = 0;
  }
  if (w.res.souls > w.resCap * 2) w.res.souls = w.resCap * 2;
}

/**
 * Everything `stepProduction` knows about one building, without producing
 * anything. For the building panel and the Production tab.
 *
 * It is the same arithmetic as the loop above, in the same order, with the same
 * multipliers, and it is deliberately a *read* — nothing here touches `w.res`.
 * Every term is reported separately rather than pre-multiplied, because the
 * whole point of the panel is that a player can see which of staffing, tier,
 * placement, season and supply is the one costing them.
 */
export interface ProductionReadout {
  /** True if this building converts labour into goods at all. */
  produces: boolean;
  workers: number;
  workerSlots: number;
  /** workers/slots, or 1 for something that needs nobody. */
  staffing: number;
  tier: number;
  tierMul: number;
  /** The district multiplier — see `districtReasons` for why it is what it is. */
  placeMul: number;
  /** 0..1. Below 1 means inputs or finished-goods storage limit the recipe. */
  throttle: number;
  /** Which input ran out first, or '' when nothing did. */
  starvedOf: ResourceKey | '';
  /** The finished good whose remaining storage limits this recipe. */
  storageFull: ResourceKey | '';
  /** Shared explanation of whichever constraint sets the actual rate. */
  limitReason: string;
  /** True while Famine Wind is stopping the fields. */
  famine: boolean;
  /**
   * Seconds of full-rate work per head taken, 0 for everything outside the
   * Hollow, and `tollDue` is how far through the current one it is. The panel
   * that shows a Hollowing's output has an obligation to show what it is
   * costing, or the fifth Age is a surprise instead of a decision.
   */
  toll: number;
  tollDue: number;
  /** `perMin` is what it is drawing; `wantPerMin` is what the recipe asks for. */
  inputs: Array<{ k: ResourceKey; perMin: number; wantPerMin: number; held: number }>;
  /** `perMin` is what it is actually making; `bestPerMin` is fully staffed and fed. */
  outputs: Array<{ k: ResourceKey; perMin: number; bestPerMin: number; seasonMul: number }>;
  /**
   * The ground this building works, for the four that work ground.
   *
   * `harvest` is −1 for everything else. `nodesInReach` is what is still
   * standing inside `HARVEST_RADIUS`, `amountInReach` is what is left in it, and
   * `hauling` says whether an accessible source is available. Empty or blocked
   * camps report zero output; displayed working rates are nominal before travel.
   */
  harvest: number;
  hauling: boolean;
  nodesInReach: number;
  amountInReach: number;
}

/** Live accessible sources and remaining reserves, shared with placement and claims. */
export function nodesInReach(w: World, site: number, kind: number): { count: number; amount: number } {
  const def = buildingType(w.buildings.typeId[site]);
  const sources = harvestSourcesAtPlacement(w, kind, w.buildings.tx[site], w.buildings.ty[site], def.w, def.h);
  return { count: sources.length, amount: sources.reduce((amount, s) => amount + w.nodes.amount[s.node], 0) };
}

/**
 * What this villager has on his back, as a resource key, or '' for empty hands.
 *
 * The decode half of `UnitStore.carryKind`'s plus-one encoding, in one place, so
 * a renderer drawing a man with a log on his shoulder and the simulation
 * granting the log cannot disagree about which log it is.
 */
export function carriedResource(w: World, i: number): ResourceKey | '' {
  const c = w.units.carryKind[i];
  return c > 0 ? RESOURCE_KEYS[c - 1] ?? '' : '';
}

/** Has the player asked for this one specifically? See `World.repairOrders`. */
export function isRepairOrdered(w: World, i: number): boolean {
  return w.repairOrders.includes(i);
}

/** Everything damaged and player-owned, ordered first. For a repair panel. */
export interface RepairReadout {
  index: number;
  typeIdx: number;
  name: string;
  hp: number;
  maxHp: number;
  /** 0..1. */
  fraction: number;
  ordered: boolean;
  /** What mending it the rest of the way will cost, at `REPAIR_COST_FRACTION`. */
  cost: Partial<ResourceBundle>;
}

/**
 * The same readout for **one** building, or null if it wants no repair.
 *
 * `repairReport` walks every building in the town and allocates a cost bundle
 * for each, which is right for a panel that lists them all and quite wrong for
 * the building panel — that runs every frame for the one building the player
 * has open, and a full-store walk sixty times a second to answer a question
 * about a single slot is the sort of thing that only shows up on a big town.
 *
 * `repairReport` is built on this, so there is still exactly one authority for
 * what a repair costs and whether it is wanted. The greyed button, the quoted
 * price and the storehouse's actual bill are the same arithmetic.
 */
export function repairQuote(w: World, i: number): RepairReadout | null {
  const b = w.buildings;
  if (i < 0 || i >= b.slots.high) return null;
  if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) return null;
  if (b.build[i] < 1) return null;
  if (b.hp[i] >= b.maxHp[i] - 0.5) return null;
  const def = buildingType(b.typeId[i]);
  const missing = 1 - b.hp[i] / Math.max(1, b.maxHp[i]);
  const cost: Partial<ResourceBundle> = {};
  for (const k of RESOURCE_KEYS) {
    const c = (def.cost[k] ?? 0) * REPAIR_COST_FRACTION * missing;
    if (c > 0) cost[k] = c;
  }
  return {
    index: i,
    typeIdx: b.typeId[i],
    name: def.name,
    hp: b.hp[i],
    maxHp: b.maxHp[i],
    fraction: b.hp[i] / Math.max(1, b.maxHp[i]),
    ordered: w.repairOrders.includes(i),
    cost,
  };
}

export function repairReport(w: World): RepairReadout[] {
  const b = w.buildings;
  const out: RepairReadout[] = [];
  for (let i = 0; i < b.slots.high; i++) {
    const q = repairQuote(w, i);
    if (q !== null) out.push(q);
  }
  // Ordered first, then the worst hurt. A panel that lists them in slot order is
  // a panel nobody reads twice.
  out.sort((p, q) => (p.ordered === q.ordered ? p.fraction - q.fraction : p.ordered ? -1 : 1));
  return out;
}

export function productionReadout(w: World, i: number, dt = TICK_DT): ProductionReadout {
  const b = w.buildings;
  const def = buildingType(b.typeId[i]);
  const famine = waveModifier(w, 'famine');

  const working = b.build[i] >= 1 && (def.workers === 0 || b.workers[i] > 0);
  const staffing = def.workers > 0 ? b.workers[i] / def.workers : 1;
  const tierMul = 1 + (b.tier[i] - 1) * 0.45;
  const placeMul = districtBonus(w, i);
  const { throttle, starvedOf, storageFull, limitReason } = productionThrottle(w, def, staffing, tierMul, placeMul, dt);

  const inputs: ProductionReadout['inputs'] = [];
  for (const k of RESOURCE_KEYS) {
    const need = def.consumes[k] ?? 0;
    if (need <= 0) continue;
    inputs.push({
      k,
      perMin: working ? need * staffing * tierMul * throttle * 60 : 0,
      wantPerMin: need * tierMul * 60,
      held: w.res[k],
    });
  }

  const harvest = siteHarvestKind(def);
  const reach = harvest >= 0 ? nodesInReach(w, i, harvest) : { count: 0, amount: 0 };
  // The same two questions `stepProduction` asks, in the same order: is there
  // anything there, and has anybody been able to get to it.
  const hauling = harvest >= 0 && reach.count > 0 && !campStalled(w, i);

  const outputs: ProductionReadout['outputs'] = [];
  for (const k of RESOURCE_KEYS) {
    const out = def.produces[k] ?? 0;
    if (out <= 0) continue;
    const stopped = k === 'grain' && famine;
    const common = recipeOutputRate(w, def, k, tierMul, placeMul) * 60;
    outputs.push({
      k,
      perMin: stopped || !working || (harvest >= 0 && !hauling) ? 0 : staffing * throttle * common,
      bestPerMin: stopped ? 0 : common,
      seasonMul: seasonYield(w, k),
    });
  }

  return {
    produces: outputs.length > 0 || inputs.length > 0,
    workers: b.workers[i],
    workerSlots: def.workers,
    staffing,
    tier: b.tier[i],
    tierMul,
    placeMul,
    throttle,
    starvedOf: inputs.length > 0 ? starvedOf : '',
    storageFull,
    limitReason,
    famine,
    toll: def.toll,
    tollDue: def.toll > 0 ? b.stock[i] : 0,
    inputs,
    outputs,
    harvest,
    hauling,
    nodesInReach: reach.count,
    amountInReach: reach.amount,
  };
}

/**
 * The purse.
 *
 * Taxes come off the *people*, not the ground: population times the band you
 * have set, scaled by how well the town is doing. Wages go out to every soldier
 * standing, every second, whether or not there is anything to fight.
 *
 * The interesting part is what happens when the two do not meet. An empty
 * treasury does not simply stop — it goes into arrears, and after about half a
 * minute of unpaid wages soldiers begin to walk. That is the pressure that
 * makes the economy matter to the *army*, and it is why "recruit everything"
 * stops being the right answer somewhere around the fourth company.
 */
/**
 * How well the town is doing, as the tax collector sees it. 0.35 … 1.45.
 *
 * Exported because the Realm tab quotes it: "you are taking 62% of what this
 * band could give you" is a sentence the player can act on, and it is the term
 * that makes morale worth money rather than worth nothing.
 */
export function prosperity(w: World): number {
  // Variety is worth a third of what bread is, and it is worth *something*
  // deliberately: without it a second food is a second bar and nothing else,
  // and the owner asked for kinds rather than quantities.
  return clamp(0.55 + w.morale * 0.5 + w.breadShare * 0.15 + w.dietVariety * 0.05, 0.35, 1.45);
}

/** What a given notch of the dial would yield, in gold per minute, right now. */
export function taxProjection(w: World, policy: number): number {
  return w.pop * taxBand(policy).goldPerHeadPerMin * prosperity(w) * w.difficulty.incomeMul;
}

function stepTreasury(w: World, dt: number): void {
  const u = w.units;
  const taxPerMin = taxProjection(w, w.taxPolicy);

  let wagePerMin = 0;
  let onPayroll = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    const wage = unitWage(unitType(u.typeId[i]));
    if (wage <= 0) continue;
    wagePerMin += wage;
    onPayroll++;
  }

  w.econ.taxIncome = taxPerMin;
  w.econ.wageBill = wagePerMin;
  w.econ.onPayroll = onPayroll;
  w.econ.net = taxPerMin + w.econ.tradeIncome - wagePerMin;

  // Trade income has already been banked by stepProduction; only the tax and
  // the wages move the pile here.
  w.res.gold += ((taxPerMin - wagePerMin) * dt) / 60;

  if (w.res.gold < 0) {
    w.res.gold = 0;
    w.arrears += dt;
    if (w.arrears >= ARREARS_DESERTION_SECONDS) {
      w.arrears -= ARREARS_DESERTION_SECONDS;
      desert(w);
    }
  } else {
    // Paying up settles the grudge, but not instantly.
    w.arrears = Math.max(0, w.arrears - dt * 1.5);
  }
}

/**
 * Somebody goes home. The largest company loses a model, because a big unpaid
 * company is exactly where the muttering starts, and because taking the last
 * man from a two-man company would quietly delete it.
 */
function desert(w: World): void {
  let best: (typeof w.companies)[number] | null = null;
  for (const c of w.companies) {
    if (c.team !== Team.Player || c.dead) continue;
    if (c.members.length <= 1) continue;
    if (!best || c.members.length > best.members.length) best = c;
  }
  if (!best) return;
  const h = best.members[best.members.length - 1];
  const i = w.units.resolve(h);
  if (i < 0) return;
  const name = unitType(w.units.typeId[i]).name;
  w.units.kill(i);
  pushLog(w.events, `A ${name} of ${best.name} goes home unpaid. Word will get round.`, LogTone.Bad);
  w.morale -= 0.02;
}

/**
 * Food. Bread is eaten first and is worth two and a half times its weight in
 * grain, so a working bakery is the difference between feeding a village and
 * feeding a city. A settlement eating bread is also a happier one.
 */
/**
 * How much grain the mouths want a minute, at this season's rate.
 *
 * The largest single draw in the game and, until the Ledger, the only one with
 * no number anywhere: the food tooltip said what fraction of it was met and
 * never what it was. Exported so `stepUpkeep` and the People tab quote the same
 * figure rather than two arrangements of the same three multipliers.
 */
export function grainUpkeepMul(w: World): number {
  // Hardfrost is a third more food for the same number of mouths. Multiplied in
  // rather than added, so the Pale Litany — whose upkeep multiplier is zero —
  // still eats nothing in winter, and the branch in `stepUpkeep` still catches it.
  return (w.faction.bonuses.grainUpkeep ?? 1) * w.mods.grainUpkeep * seasonUpkeep(w);
}

/**
 * What one mouth wants a minute, in grain-equivalents, at this population.
 *
 * **The rule the owner asked for:** *"the more people yo ahve the more food you
 * use"*. It already used more in total — the bill was `pop × a constant` — and
 * that is precisely what made it feel like nothing: a settlement that doubles
 * its people doubles its farms, and a term that is linear in population is a
 * term the player can outrun by repeating one building.
 *
 * So the *rate itself* climbs, in rungs of `POP_PER_RUNG` mouths, and this is
 * the settlement speaking the campaign's language rather than a second one:
 * `campaign/food.ts` charges `FOOD_PER_HOLDING` for the ground and
 * `FOOD_PER_TIER` again for every rung the holding has been built up, on the
 * stated argument that the ladder must not be free. Population *is* the
 * settlement's ladder, so it is the settlement's rung.
 */
export function foodPerMouthPerMin(pop: number): number {
  const rungs = Math.floor(Math.max(0, pop) / POP_PER_RUNG);
  return GRAIN_PER_POP_PER_MIN + FOOD_PER_MOUTH_PER_RUNG * rungs;
}

/**
 * The whole appetite a minute, in grain-equivalents.
 *
 * Named for grain and no longer paid in it alone — see `FOOD_VALUE`. The name
 * is kept because two screens and a tooltip quote this function and the unit it
 * returns has not changed: a grain-equivalent is still a grain.
 */
export function grainDemandPerMin(w: World): number {
  return w.pop * foodPerMouthPerMin(w.pop) * grainUpkeepMul(w);
}

/**
 * What is actually on the shelves, in grain-equivalents, kind by kind.
 *
 * Derived, exported, and used by the Ledger's flow table and by the alert
 * cluster, so all three read one larder rather than three arrangements of the
 * same four numbers.
 */
export function larder(w: World): Array<{ k: (typeof FOOD_KEYS)[number]; held: number; worth: number }> {
  return FOOD_KEYS.map((k) => ({ k, held: w.res[k], worth: w.res[k] * FOOD_VALUE[k] }));
}

/**
 * What a minute of the town'''s appetite would actually take off each shelf.
 *
 * The same richest-first walk `stepUpkeep` does, run once over a minute'''s worth
 * instead of a tick'''s, so the Ledger'''s flow table charges the draw to the goods
 * it is really coming out of. Pure — it reads the stores and writes nothing.
 */
export function foodDrawPerMin(w: World): Array<[ResourceKey, number]> {
  let need = grainDemandPerMin(w);
  const out: Array<[ResourceKey, number]> = [];
  for (const k of FOOD_KEYS) {
    if (need <= 1e-9) break;
    const take = Math.min(w.res[k], need / FOOD_VALUE[k]);
    if (take <= 0) continue;
    need -= take * FOOD_VALUE[k];
    out.push([k, take]);
  }
  // Whatever is still wanted is charged to grain, which is what a town short of
  // everything is short *of* — a shortfall booked nowhere reads as a balanced
  // ledger, which is the one thing this table must never say.
  if (need > 1e-9) out.push(['grain', need]);
  return out;
}

/** Grain-equivalents on the shelves, all kinds together. */
export function larderWorth(w: World): number {
  let sum = 0;
  for (const k of FOOD_KEYS) sum += w.res[k] * FOOD_VALUE[k];
  return sum;
}

/**
 * Which kinds of food the town is out of, richest first — for the alert.
 *
 * Only ever asked when the town is short, so "the granary is empty" is
 * information rather than noise: a settlement that has never built a weir is
 * not *short of fish*, it simply does not fish, and a kind nothing produces is
 * left out. `everProduced` is the test, and it is asked of the standing
 * buildings rather than remembered, so demolishing every weir stops the alert
 * mentioning them.
 */
export function foodKindsShort(w: World): Array<(typeof FOOD_KEYS)[number]> {
  const out: Array<(typeof FOOD_KEYS)[number]> = [];
  for (const k of FOOD_KEYS) {
    if (w.res[k] > 0.5) continue;
    if (k === 'grain' || producesFood(w, k)) out.push(k);
  }
  return out;
}

/** Does anything standing (or being built) make this food? */
function producesFood(w: World, k: (typeof FOOD_KEYS)[number]): boolean {
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.team[i] !== Team.Player) continue;
    if ((buildingType(b.typeId[i]).produces[k] ?? 0) > 0) return true;
  }
  return false;
}

/**
 * The town eats.
 *
 * Four kinds now, eaten richest first — which is the rule bread has always
 * followed, generalised rather than replaced. `FOOD_VALUE` is the exchange rate
 * and `FOOD_KEYS` is the order; adding a fifth food is a line in
 * `sim/constants.ts` and a building, and nothing here changes.
 *
 * `breadShare` keeps its exact old meaning — bread's share of the *whole*
 * appetite — because the two things that read it (prosperity, and the Hearth's
 * growth rate) were tuned against that number and a silent redefinition would
 * have moved both without a line of either file changing. What variety buys is
 * `dietVariety`, which is new, and small.
 */
function stepUpkeep(w: World, dt: number): void {
  const upkeepMul = grainUpkeepMul(w);
  if (upkeepMul <= 0) {
    // The Pale Litany eats nothing and pays in Souls instead.
    w.res.souls = Math.max(0, w.res.souls - w.pop * 0.004 * dt);
    w.fedFraction = 1;
    // A people that eats nothing has no table to vary. Written rather than left
    // where it was, so a world that changes race mid-run cannot carry a stale
    // one into the digest.
    w.dietVariety = 0;
    return;
  }
  let need = (w.pop * foodPerMouthPerMin(w.pop) * upkeepMul * dt) / 60;
  const wanted = Math.max(1e-6, need);

  /**
   * Variety is read off the **larder**, not off this tick's meal.
   *
   * The first cut counted the kinds the appetite was actually drawn from, and it
   * was wrong in a way worth recording: the larder is spent richest first, so a
   * town with all four shelves full eats nothing but bread and scored zero.
   * That is the mechanic contradicting the fiction — the table *is* varied, the
   * granary is simply not being touched this thirtieth of a second.
   *
   * So the term asks what is in store: a kind counts when it holds a fifth of a
   * minute's appetite or more, which is enough to be a dish rather than a
   * garnish. Normalised over all four kinds and not over the kinds this
   * settlement happens to produce — a village eating nothing but grain is eating
   * one thing whether or not it has ever heard of fish, and grading a people
   * against its own ambitions makes a term that can be neither earned nor lost.
   *
   * Computed before the meal is taken, so a shelf emptied by this very tick
   * still counts for it; the next tick will find it bare and say so.
   */
  const perMinute = Math.max(1e-6, (wanted * 60) / Math.max(1e-6, dt));
  let kinds = 0;
  for (const k of FOOD_KEYS) {
    if (w.res[k] * FOOD_VALUE[k] >= perMinute * 0.2) kinds++;
  }
  w.dietVariety = clamp01((Math.max(1, kinds) - 1) / (FOOD_KEYS.length - 1));

  let breadFrom = 0;
  for (const k of FOOD_KEYS) {
    if (need <= 1e-9) break;
    const value = FOOD_VALUE[k];
    const take = Math.min(w.res[k], need / value);
    if (take <= 0) continue;
    w.res[k] -= take;
    need -= take * value;
    if (k === 'bread') breadFrom = take * value;
  }

  w.breadShare = Math.min(1, breadFrom / wanted);

  if (need <= 1e-9) {
    w.fedFraction = 1;
  } else {
    w.fedFraction = clamp01(1 - need / wanted);
    w.morale -= 0.025 * dt * (1 - w.fedFraction);
    if (w.tick % 90 === 0) {
      const short = foodKindsShort(w);
      const named = short.map((k) => RESOURCE_LABELS[k].toLowerCase()).join(', ');
      pushLog(
        w.events,
        short.length > 0
          ? `Nothing left of the ${named}. People are counting their neighbours.`
          : 'The larder is short. People are counting their neighbours.',
        LogTone.Bad,
      );
    }
  }
}

function stepPopulation(w: World, dt: number): void {
  const u = w.units;
  let villagers = 0;
  let soldiers = 0;
  /**
   * The pool the host is standing in, counted on the same pass that counts heads.
   *
   * A separate loop would have been a second walk of the unit store every tick
   * for a number one race in eleven reads, so it rides along here: the cost is
   * one multiply per soldier and the answer is exact rather than accumulated,
   * which is the same discipline `w.pop` is under and for the same reason —
   * anything accumulated on spawn and decremented on death drifts the first time
   * a model leaves the world by a path somebody forgot about.
   *
   * A model's Mana cost is its `threat`, which is already the game's one honest
   * measure of how much trouble a soldier is and is what wages are derived from
   * as well. So a Litany pool buys the same *amount of army* whichever units it
   * is spent on, and the choice between six Wretches and one Crypt Knight stays
   * a tactical one rather than an arbitrage.
   */
  let mana = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) !== 0) villagers++;
    else if ((u.flags[i] & UF.Summoned) === 0) {
      soldiers++;
      mana += unitType(u.typeId[i]).threat * MANA_PER_THREAT;
    }
  }
  w.pop = villagers + soldiers;
  w.mana = Math.round(mana * 100) / 100;
  const stats = statsOf(w);
  if (w.pop > stats.peakPop) stats.peakPop = w.pop;
  w.counts.villagers = villagers;

  /**
   * Villagers stop breeding well short of the cap.
   *
   * Without this they fill every bed in the settlement and there is physically
   * no room left to raise a company — the harness produced runs that reached
   * wave ten with a thriving town, a finished barracks and not one soldier.
   * The reserved share is the army's, and the player should be able to see that
   * in the tooltip rather than discover it at the gate.
   */
  /**
   * The warren breeds on its own, and that is the entire departure.
   *
   * Three of the four terms above are lifted: no cap, because the Splitmoot has
   * none; no build-phase gate, because goblins do not observe one; and the rate
   * *rises* with how many there already are rather than stopping short of a
   * ceiling. The one term kept is food — a warren with an empty granary still
   * stops, because a people who could breed out of nothing would have no economy
   * at all and the press would be a stopwatch rather than a decision.
   *
   * `sqrt` rather than a straight multiple: linear compounding takes a warren
   * from forty to four hundred inside a wave and the press stops being a curve
   * you manage and becomes a cliff you fall off. At the root it roughly doubles
   * between a hamlet and a city, which is fast enough to feel like something
   * getting away from you.
   */
  const moot = race(w.raceId).binding === 'moot';
  if (moot) {
    if (fed(w)) {
      const heat = Math.sqrt(Math.max(1, villagers)) * (0.4 + w.morale * 0.3);
      w.buildings.stock[Math.max(0, w.hearth)] += dt * heat;
      if (w.buildings.stock[Math.max(0, w.hearth)] >= MOOT_BREED_SECONDS) {
        w.buildings.stock[Math.max(0, w.hearth)] = 0;
        const ang = (w.tick % 360) * 0.0174533;
        spawnUnit(
          w,
          VILLAGER_TYPE,
          Team.Player,
          w.map.hearthX + 0.5 + Math.cos(ang) * 2.4,
          w.map.hearthY + 0.5 + Math.sin(ang) * 2.4,
        );
        w.dirty.jobs = true;
      }
    }
    return;
  }

  const villagerCap = Math.max(6, Math.floor(w.popCap * VILLAGER_SHARE));
  if (w.phase !== Phase.Siege && villagers < villagerCap && fed(w)) {
    w.buildings.stock[Math.max(0, w.hearth)] +=
      dt * (1 + w.breadShare + w.dietVariety * 0.25) * (0.6 + w.morale * 0.6);
    if (w.buildings.stock[Math.max(0, w.hearth)] >= POP_GROWTH_SECONDS) {
      w.buildings.stock[Math.max(0, w.hearth)] = 0;
      const ang = (w.tick % 360) * 0.0174533;
      // A head arriving to fill a bed the player built is a settler and arrives
      // grown. It is not a birth, and booking it as one starves the workforce
      // of the town's entire growth — the argument, and what it measured, is in
      // `src/data/demography.ts`. What the town breeds is a rate of its own,
      // taken in `stepDemography`.
      spawnUnit(
        w,
        VILLAGER_TYPE,
        Team.Player,
        w.map.hearthX + 0.5 + Math.cos(ang) * 2.4,
        w.map.hearthY + 0.5 + Math.sin(ang) * 2.4,
      );
      w.dirty.jobs = true;
    }
  }
}

/**
 * Every term in the morale sum, named and signed.
 *
 * This is the arithmetic `stepMoraleAggregate` runs — it now runs *this*, so
 * the two cannot say different things. The People tab prints the list, which
 * turns "morale is 41%" from a verdict into a diagnosis: a player who can see
 * that the tax band is costing twenty points and the empty granary another
 * fifteen knows which of the two to fix first.
 */
export interface MoraleTerm {
  label: string;
  /** Signed contribution to the target, in morale points (0..1.4 scale). */
  value: number;
  note: string;
}

export function moraleTerms(w: World): MoraleTerm[] {
  const b = w.buildings;
  let comforts = 0;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    comforts += buildingType(b.typeId[i]).morale;
  }
  const hearthHealth =
    w.hearth >= 0 && b.slots.alive[w.hearth] === 1 ? b.hp[w.hearth] / Math.max(1, b.maxHp[w.hearth]) : 0;
  const crowding = w.popCap > 0 ? clamp01(1 - Math.max(0, w.pop - w.popCap) / Math.max(1, w.popCap)) : 1;

  return [
    { label: 'Being alive at all', value: 0.18, note: 'The floor. It is not much.' },
    { label: 'Comforts', value: comforts, note: 'Every chapel, tavern, bell and gallows, summed.' },
    {
      label: 'The Hearth',
      value: hearthHealth * 0.4,
      note: hearthHealth >= 1 ? 'Burning as it should.' : 'Damaged. They can see it from the street.',
    },
    { label: 'Fed', value: w.fedFraction * 0.18, note: `${(w.fedFraction * 100).toFixed(0)}% of the appetite met.` },
    { label: 'Bread rather than grain', value: w.breadShare * 0.12, note: 'An oven is worth more than a field.' },
    {
      label: 'A varied table',
      value: w.dietVariety * 0.04,
      note: 'Bread, meat, fish and grain. Four is a feast; one is a ration.',
    },
    { label: `Tax — ${taxBand(w.taxPolicy).name}`, value: taxBand(w.taxPolicy).morale, note: taxBand(w.taxPolicy).note },
    {
      label: 'Unpaid wages',
      value: w.arrears > 0 ? -Math.min(0.25, w.arrears * 0.01) : 0,
      note: w.arrears > 0 ? `${w.arrears.toFixed(0)}s in arrears. They are counting.` : 'The purse has met the bill.',
    },
    {
      label: 'Crowding',
      value: -(1 - crowding) * 0.3,
      note: w.pop > w.popCap ? 'More people than beds.' : 'Everybody has somewhere to sleep.',
    },
  ];
}

/** Where morale is heading, as opposed to where it is. */
export function moraleTarget(w: World): number {
  let sum = 0;
  for (const t of moraleTerms(w)) sum += t.value;
  return clamp(sum, 0.05, 1.4);
}

function stepMoraleAggregate(w: World, dt: number): void {
  const target = moraleTarget(w);
  w.morale += (target - w.morale) * Math.min(1, 0.12 * dt);
  w.morale = clamp(w.morale, 0, 1.4);
}

export function resourceLabel(k: ResourceKey): string {
  return k;
}
